/**
 * Temps de simulation et calendrier.
 *
 * Deux échelles coexistent (voir DECISIONS.md) :
 * - le temps de simulation (`simTime`, secondes) fait avancer skieurs, câbles
 *   et files d'attente à une vitesse visuellement naturelle ;
 * - l'horloge de jeu (`gameTime`, secondes depuis le début de saison) est
 *   accélérée : une heure de jeu dure 3 minutes en journée (vitesse x1). La
 *   nuit passe environ 2 fois plus vite tant que les dameuses travaillent, puis 30 fois
 *   plus vite une fois la dernière rentrée au garage.
 */

/** Fréquence de la logique serveur. */
export const TICK_HZ = 10;
export const TICK_DT = 1 / TICK_HZ;

/** Secondes de jeu par seconde de simulation, en journée. */
export const DAY_SCALE = 20;
/** Secondes de jeu par seconde de simulation, la nuit (station fermée). */
export const NIGHT_SCALE = 600;
/**
 * Secondes de jeu par seconde de simulation la nuit tant qu'une dameuse est
 * dehors : assez lent pour suivre le damage à l'écran (voir DECISIONS.md).
 */
export const GROOM_SCALE = 45;

export const DAY_SECONDS = 86_400;
export const OPEN_MINUTE = 8 * 60 + 30;
export const LAST_LIFT_MINUTE = 16 * 60 + 45;
export const CLOSE_MINUTE = 17 * 60;
/** Fin de journée : la nuit accélérée commence quand le domaine est vide. */
export const NIGHT_MINUTE = 17 * 60 + 30;
/** Retour à l'échelle de jour. */
export const MORNING_MINUTE = 7 * 60 + 45;
/** Début de partie : jour 1 à 8 h 20, dix minutes avant l'ouverture. */
export const START_GAME_TIME = 8 * 3600 + 20 * 60;

/** Vitesses proposées : pause, ralenti (¼, ½), normale, accélérée jusqu'à ×32. */
export const SPEEDS = [0, 0.25, 0.5, 1, 2, 4, 8, 16, 32] as const;
export type SimSpeed = (typeof SPEEDS)[number];

/**
 * Pas de simulation maximal (s). Au-delà de ×4, le serveur découpe chaque tick
 * en plusieurs pas de cette durée : les systèmes à pas fixe gardent la même
 * précision qu'à ×4.
 */
export const MAX_STEP = 0.4;

/** « ×¼ », « ×½ », « ×4 ». */
export function speedLabel(speed: number): string {
  if (speed === 0.25) return '\u00d7\u00bc';
  if (speed === 0.5) return '\u00d7\u00bd';
  return `\u00d7${speed}`;
}

/** Vitesse voisine dans la liste (dir = +1 plus vite, -1 plus lent), sans passer par la pause. */
export function stepSpeed(speed: number, dir: 1 | -1): SimSpeed {
  const list = SPEEDS.filter((s) => s > 0);
  let i = list.findIndex((s) => s >= speed);
  if (i < 0) i = list.length - 1;
  else if (list[i] !== speed && dir < 0) i--;
  else if (list[i] === speed) i += dir;
  return list[Math.max(0, Math.min(list.length - 1, i))] as SimSpeed;
}

export function minuteOfDay(gameTime: number): number {
  return (((gameTime % DAY_SECONDS) + DAY_SECONDS) % DAY_SECONDS) / 60;
}

/** Jour de saison, à partir de 1. */
export function dayIndex(gameTime: number): number {
  return Math.floor(gameTime / DAY_SECONDS) + 1;
}

export function isNightMinute(m: number): boolean {
  return m >= NIGHT_MINUTE || m < MORNING_MINUTE;
}

export function isOpenMinute(m: number): boolean {
  return m >= OPEN_MINUTE && m < LAST_LIFT_MINUTE;
}

/** Heures de jeu écoulées pendant `simSeconds` à l'échelle `scale`. */
export function gameHours(simSeconds: number, scale: number): number {
  return (simSeconds * scale) / 3600;
}

/** Secondes de simulation correspondant à des minutes de jeu (échelle de jour). */
export function simSecondsForGameMinutes(minutes: number, scale = DAY_SCALE): number {
  return (minutes * 60) / scale;
}

const MONTHS = [
  { name: 'déc.', days: 31 },
  { name: 'janv.', days: 31 },
  { name: 'févr.', days: 28 },
  { name: 'mars', days: 31 },
  { name: 'avr.', days: 30 },
];
const WEEKDAYS = ['dim.', 'lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.'];
/** Le jour 1 de la saison est le samedi 12 décembre. */
const START_DAY_OF_MONTH = 12;
const START_WEEKDAY = 6;

export interface CalendarDay {
  day: number;
  month: number;
  dayOfMonth: number;
  weekday: number;
  label: string;
  weekend: boolean;
  holiday: string | null;
}

export function calendarDay(day: number): CalendarDay {
  let offset = day - 1 + (START_DAY_OF_MONTH - 1);
  let month = 0;
  while (month < MONTHS.length - 1 && offset >= (MONTHS[month] as { days: number }).days) {
    offset -= (MONTHS[month] as { days: number }).days;
    month++;
  }
  const dayOfMonth = offset + 1;
  const weekday = (START_WEEKDAY + day - 1) % 7;
  const weekend = weekday === 0 || weekday === 6;
  let holiday: string | null = null;
  // Vacances scolaires approximatives (zones confondues).
  if ((month === 0 && dayOfMonth >= 19) || (month === 1 && dayOfMonth <= 3)) holiday = 'Vacances de Noël';
  else if ((month === 2 && dayOfMonth >= 6) || (month === 3 && dayOfMonth <= 7)) holiday = "Vacances d'hiver";
  const m = MONTHS[month] as { name: string };
  return {
    day,
    month,
    dayOfMonth,
    weekday,
    label: `${WEEKDAYS[weekday]} ${dayOfMonth} ${m.name}`,
    weekend,
    holiday,
  };
}

/** « Jour 3 · 14 h 20 ». */
export function formatClock(gameTime: number): string {
  const m = Math.floor(minuteOfDay(gameTime));
  const h = Math.floor(m / 60);
  const mm = String(m % 60).padStart(2, '0');
  return `Jour ${dayIndex(gameTime)} \u00b7 ${h} h ${mm}`;
}

export function formatHour(minutes: number): string {
  const m = Math.floor(minutes);
  return `${Math.floor(m / 60)} h ${String(m % 60).padStart(2, '0')}`;
}
